"use client";

import { AppError } from "@/lib/utils/errors";
import { useBusinessInfoStore } from "@/modules/store/businessInfoStore";
import {
  DashboardService,
  DashboardReportQuery,
} from "@/modules/services/dashboard/dashboardService";
import { jobService } from "@/modules/services/job/jobService";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  bucketStartMap,
  chartColors,
  emptyPipeline,
} from "@/modules/utils/dashboard/constants";
import {
  AiDistributionPoint,
  FunnelPoint,
  JobOption,
  JobReport,
  JobTypePerformancePoint,
  KpiItem,
  PipelinePoint,
  RadarPoint,
  ScoreBucketRange,
} from "@/modules/types/dashboard";

const formatNumber = (value: number) => value.toLocaleString("en-US");

export const useRecruitmentDashboard = () => {
  const { businessInfo } = useBusinessInfoStore();
  const [reports, setReports] = useState<JobReport[]>([]);
  /** From API root: today's interviews for the business (not summed from job rows). */
  const [interviewsToday, setInterviewsToday] = useState(0);
  const [jobOptions, setJobOptions] = useState<JobOption[]>([]);
  const [isLoadingApi, setIsLoadingApi] = useState(false);

  const loadDashboardReports = useCallback(
    async (query: Omit<DashboardReportQuery, "businessId"> = {}) => {
      if (!businessInfo?.id) return;

      try {
        setIsLoadingApi(true);
        const dashboardSvc = new DashboardService();
        const response = await dashboardSvc.getReports({
          businessId: businessInfo.id,
          ...query,
        });
        setReports(response.jobReports || []);
        setInterviewsToday(response.interviewsToday);
      } catch (error) {
        const appErr = new AppError(error);
        appErr.logError(
          "src/modules/hooks/dashboard/useRecruitmentDashboard.ts[loadDashboardReports]",
        );
      } finally {
        setIsLoadingApi(false);
      }
    },
    [businessInfo?.id],
  );

  useEffect(() => {
    loadDashboardReports();
  }, [loadDashboardReports]);

  const loadJobOptions = useCallback(async () => {
    if (!businessInfo?.id) return;

    try {
      const svc = new jobService();
      const resp = await svc.getsByBusiness(businessInfo.id, undefined, 1, 500);
      setJobOptions(
        (resp.jobs ?? []).map((job) => ({
          id: job.id,
          title: job.jobTitle,
        })),
      );
    } catch (error) {
      const appErr = new AppError(error);
      appErr.logError(
        "src/modules/hooks/dashboard/useRecruitmentDashboard.ts[loadJobOptions]",
      );
      setJobOptions([]);
    }
  }, [businessInfo?.id]);

  useEffect(() => {
    loadJobOptions();
  }, [loadJobOptions]);

  const allDates = useMemo(
    () => reports.map((item) => item.date).sort(),
    [reports],
  );
  const initialFromDate = allDates[0] ?? "";
  const initialToDate = allDates[allDates.length - 1] ?? "";

  const [fromDate, setFromDate] = useState(initialFromDate);
  const [toDate, setToDate] = useState(initialToDate);
  const [selectedJobId, setSelectedJobId] = useState("all");

  const filteredReports: JobReport[] = useMemo(() => {
    return reports.filter((item) => {
      const inFromRange = fromDate ? item.date >= fromDate : true;
      const inToRange = toDate ? item.date <= toDate : true;
      const inJobRange =
        selectedJobId === "all" || item.jobId === selectedJobId;
      return inFromRange && inToRange && inJobRange;
    });
  }, [fromDate, reports, selectedJobId, toDate]);

  const hasData = filteredReports.length > 0;

  const dashboardStats = useMemo(() => {
    const totals = filteredReports.reduce(
      (acc, item) => {
        acc.totalViews += item.views;
        acc.totalApplications += item.applications;
        acc.totalNewCandidates += item.newCandidates;
        acc.totalAiScore +=
          item.aiScoreBuckets.reduce((bucketSum, bucket) => {
            const midpoint = bucketStartMap[bucket.range] + 10;
            return bucketSum + midpoint * bucket.count;
          }, 0) / Math.max(item.applications, 1);
        acc.pipelines.new += item.pipeline.new;
        acc.pipelines.reviewing += item.pipeline.reviewing;
        acc.pipelines.processing += item.pipeline.processing;
        acc.pipelines.discarded += item.pipeline.discarded;
        acc.pipelines.hired += item.pipeline.hired;
        acc.distinctJobs.add(item.jobId);
        return acc;
      },
      {
        totalViews: 0,
        totalApplications: 0,
        totalNewCandidates: 0,
        totalAiScore: 0,
        pipelines: { ...emptyPipeline },
        distinctJobs: new Set<string>(),
      },
    );

    const reportCount = filteredReports.length;
    const averageAiScore =
      reportCount > 0 ? totals.totalAiScore / reportCount : 0;
    const conversionRate =
      totals.totalViews > 0
        ? (totals.totalApplications / totals.totalViews) * 100
        : 0;

    return { ...totals, averageAiScore, conversionRate };
  }, [filteredReports]);

  const funnelData: FunnelPoint[] = useMemo(() => {
    const views = dashboardStats.totalViews;
    const applications = dashboardStats.totalApplications;

    return [
      { stage: "viewed", value: views },
      { stage: "applied", value: applications },
    ];
  }, [dashboardStats.totalApplications, dashboardStats.totalViews]);

  const pipelineData: PipelinePoint[] = useMemo(
    () => [
      {
        name: "new",
        value: dashboardStats.pipelines.new,
        color: chartColors.blue,
      },
      {
        name: "reviewing",
        value: dashboardStats.pipelines.reviewing,
        color: chartColors.amber,
      },
      {
        name: "processing",
        value: dashboardStats.pipelines.processing,
        color: chartColors.purple,
      },
      {
        name: "discarded",
        value: dashboardStats.pipelines.discarded,
        color: chartColors.danger,
      },
      {
        name: "hired",
        value: dashboardStats.pipelines.hired,
        color: chartColors.success,
      },
    ],
    [dashboardStats.pipelines],
  );

  const activeCandidates = useMemo(() => {
    return (
      dashboardStats.pipelines.new +
      dashboardStats.pipelines.reviewing +
      dashboardStats.pipelines.processing
    );
  }, [dashboardStats.pipelines]);

  const jobTypePerformanceData: JobTypePerformancePoint[] = useMemo(() => {
    const grouped = new Map<string, JobTypePerformancePoint>();

    filteredReports.forEach((item) => {
      if (!grouped.has(item.jobGroup)) {
        grouped.set(item.jobGroup, {
          group: item.jobGroup,
          hot: 0,
          special: 0,
          normal: 0,
        });
      }

      const current = grouped.get(item.jobGroup);
      if (!current) return;
      const jobType = item.jobType as keyof Omit<
        JobTypePerformancePoint,
        "group"
      >;
      if (jobType in current) {
        current[jobType] += item.applications;
      }
    });

    return Array.from(grouped.values());
  }, [filteredReports]);

  const aiMatchingDistribution: AiDistributionPoint[] = useMemo(() => {
    const mergedBuckets: Record<ScoreBucketRange, number> = {
      "0-20": 0,
      "21-40": 0,
      "41-60": 0,
      "61-80": 0,
      "81-100": 0,
    };

    filteredReports.forEach((item) => {
      item.aiScoreBuckets.forEach((bucket) => {
        mergedBuckets[bucket.range] += bucket.count;
      });
    });

    return Object.entries(mergedBuckets).map(([range, count]) => ({
      range,
      count,
      score: bucketStartMap[range as ScoreBucketRange] + 10,
    }));
  }, [filteredReports]);

  const radarData: RadarPoint[] = useMemo(() => {
    if (!hasData) {
      return [
        { subject: "salary", value: 0 },
        { subject: "environment", value: 0 },
        { subject: "interview", value: 0 },
        { subject: "growth", value: 0 },
        { subject: "support", value: 0 },
      ];
    }

    const total = filteredReports.reduce(
      (acc, item) => {
        acc.salary += item.rating.salary;
        acc.environment += item.rating.environment;
        acc.process += item.rating.process;
        acc.growth += item.rating.growth;
        acc.support += item.rating.support;
        return acc;
      },
      { salary: 0, environment: 0, process: 0, growth: 0, support: 0 },
    );

    const divisor = filteredReports.length;
    return [
      { subject: "salary", value: Number((total.salary / divisor).toFixed(2)) },
      {
        subject: "environment",
        value: Number((total.environment / divisor).toFixed(2)),
      },
      {
        subject: "interview",
        value: Number((total.process / divisor).toFixed(2)),
      },
      { subject: "growth", value: Number((total.growth / divisor).toFixed(2)) },
      {
        subject: "support",
        value: Number((total.support / divisor).toFixed(2)),
      },
    ];
  }, [filteredReports, hasData]);

  const potentialZoneCount = aiMatchingDistribution.find(
    (item) => item.range === "81-100",
  )?.count;

  const kpiItems: KpiItem[] = [
    {
      labelKey: "totalJobPosts",
      value:
        dashboardStats.distinctJobs.size > 0
          ? formatNumber(dashboardStats.distinctJobs.size)
          : "0",
    },
    {
      labelKey: "newCandidates",
      value:
        dashboardStats.totalNewCandidates > 0
          ? formatNumber(dashboardStats.totalNewCandidates)
          : "0",
    },
    {
      labelKey: "avgAiMatching",
      value: `${dashboardStats.averageAiScore.toFixed(1)}%`,
    },
    {
      labelKey: "interviewsToday",
      value:
        interviewsToday > 0 ? formatNumber(interviewsToday) : "0",
    },
  ];

  const handleFromDateChange = useCallback(
    (value: string) => {
      setFromDate(value);
      loadDashboardReports({
        startDate: value,
        endDate: toDate,
        jobId: selectedJobId === "all" ? undefined : selectedJobId,
      });
    },
    [loadDashboardReports, selectedJobId, toDate],
  );

  const handleToDateChange = useCallback(
    (value: string) => {
      setToDate(value);
      loadDashboardReports({
        startDate: fromDate,
        endDate: value,
        jobId: selectedJobId === "all" ? undefined : selectedJobId,
      });
    },
    [fromDate, loadDashboardReports, selectedJobId],
  );

  const handleJobIdChange = useCallback(
    (value: string) => {
      setSelectedJobId(value);
      loadDashboardReports({
        startDate: fromDate,
        endDate: toDate,
        jobId: value === "all" ? undefined : value,
      });
    },
    [fromDate, loadDashboardReports, toDate],
  );

  return {
    fromDate,
    toDate,
    selectedJobId,
    handleFromDateChange,
    handleToDateChange,
    handleJobIdChange,
    jobOptions,
    hasData,
    kpiItems,
    funnelData,
    conversionRate: dashboardStats.conversionRate,
    pipelineData,
    activeCandidates,
    aiMatchingDistribution,
    potentialZoneCount: potentialZoneCount ?? 0,
    radarData,
    jobTypePerformanceData,
    isLoadingApi,
    loadDashboardReports,
  };
};
